import { applyInlineStyles } from '../../../utils/dom-style'
import { warn } from '../../../utils/logger'
import { type EmbedIFrameComponent } from '../../embed-component'
import { type EmbedTarget } from '../../embed-targets'
import { EmbedStyleControl } from '../../iframe/default-embed-style-control'
import { type IFrameEmbedHandler } from './IFrameEmbedStrategy'
import { computePosition, autoUpdate, type ComputePositionConfig, offset } from '@floating-ui/dom'

class IFrameStyleControl extends EmbedStyleControl {
  constructor (embedTarget: EmbedTarget, iFrameComponent: EmbedIFrameComponent) {
    super(iFrameComponent.rootEl, {
      styles: {
        overlay: () => Object.assign({
          height: `calc(100% - ${embedTarget.bottomSpace})`
        }, embedTarget.ctx.settings.style),
        teleport: Object.assign({
          height: '100%'
        }, embedTarget.ctx.settings.teleport?.style)
      }
    })
  }
}

/**
 * Limitations:
 * - requires `position: relative` on parent of the target element
 * - modifies `margin-bottom` of target element
 */
export class FloatStrategyHandler implements IFrameEmbedHandler {
  wrapperDiv: HTMLDivElement
  iframeStyleControl: IFrameStyleControl
  position: 'fixed' | 'absolute'
  #resizeWatch?: () => void
  #currentMode?: 'teleport' | 'overlay'
  watchOptions: Partial<ComputePositionConfig>
  #prevMarginBottom: string
  #teleportHeight: string
  iFrameComponent: EmbedIFrameComponent

  constructor (
    public readonly embedTarget: EmbedTarget
  ) {
    this.embedTarget = embedTarget
    this.wrapperDiv = this.embedTarget.ctx.createElement('div')
    this.iframeStyleControl = new IFrameStyleControl(this.embedTarget, this.iFrameComponent)
    this.position = this.embedTarget.ctx.settings.strategy === 'float-fixed' ? 'fixed' : 'absolute'
    this.#teleportHeight = this.embedTarget.ctx.settings.teleport?.height ?? '250px'
    this.watchOptions = {
      placement: 'bottom', // 'bottom' by default
      strategy: this.position,
      middleware: [offset(({ rects }) => {
        return {
          mainAxis: -rects.reference.height
        }
      })]
    }
  }

  async initIFrame (iFrameComponent: EmbedIFrameComponent) {
    this.iFrameComponent = iFrameComponent
    applyInlineStyles({
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      'pointer-events': 'all'
    }, this.iFrameComponent.rootEl)

    applyInlineStyles({
      width: `${this.embedTarget.ctx.targetEl.offsetWidth}px`,
      height: `${this.embedTarget.ctx.targetEl.offsetWidth}px`,
      position: this.position,
      'pointer-events': 'none'
    }, this.wrapperDiv)

    this.wrapperDiv.appendChild(this.iFrameComponent.rootEl)
    this.embedTarget.ctx.targetEl.after(this.wrapperDiv)
    this.onTeleportUpdate().catch(warn)
  }

  async #onOverlayResize () {
    const watchEl = this.embedTarget.ctx.targetEl
    const { x, y } = await computePosition(watchEl, this.wrapperDiv, this.watchOptions)
    const w = watchEl.offsetWidth
    const h = watchEl.offsetHeight
    this.wrapperDiv.style.setProperty('left', `${x}px`)
    this.wrapperDiv.style.setProperty('top', `${y}px`)
    this.wrapperDiv.style.setProperty('width', `${w}px`)
    this.wrapperDiv.style.setProperty('height', `${h}px`)
  }

  async #onTeleportResize () {
    const watchEl = this.embedTarget.ctx.targetEl
    const { x } = await computePosition(watchEl, this.wrapperDiv, this.watchOptions)
    const w = watchEl.offsetWidth

    this.wrapperDiv.style.setProperty('top', `-${this.#teleportHeight}px`)
    this.wrapperDiv.style.setProperty('width', `${w}px`)
    this.wrapperDiv.style.setProperty('height', this.#teleportHeight)
    this.wrapperDiv.style.setProperty('left', `${x}px`)
  }

  #updateWatch (type: 'teleport' | 'overlay') {
    if (this.#currentMode === type) return
    this.#resizeWatch?.()
    this.#currentMode = type
    const watchEl = this.embedTarget.ctx.targetEl
    if (type === 'overlay') {
      this.wrapperDiv.style.setProperty('position', this.position)
      watchEl.style.setProperty('margin-bottom', this.#prevMarginBottom)
      this.#onOverlayResize().catch(warn)
      this.#resizeWatch = autoUpdate(watchEl, this.wrapperDiv, () => {
        this.#onOverlayResize().catch(warn)
      })
    } else {
      this.wrapperDiv.style.setProperty('top', '')
      this.wrapperDiv.style.setProperty('position', 'relative')
      this.#prevMarginBottom = watchEl.style.getPropertyValue('margin-bottom')
      watchEl.style.setProperty('margin-bottom', this.#prevMarginBottom != null ? `calc(${this.#prevMarginBottom} + ${this.#teleportHeight}px)` : `${this.#teleportHeight}px`)
      this.#onTeleportResize().catch(warn)
      this.#resizeWatch = autoUpdate(watchEl, this.wrapperDiv, () => {
        this.#onTeleportResize().catch(warn)
      })
    }
  }

  async onTeleportUpdate () {
    const type = this.embedTarget.ctx.teleportActive === true ? 'teleport' : 'overlay'
    this.#updateWatch(type)
    this.iframeStyleControl.updateStyle(type)
  }

  show () {
    this.wrapperDiv.style.setProperty('display', '')
  }

  hide () {
    this.wrapperDiv.style.setProperty('display', 'none')
  }
}
